import React from 'react'
import type { Venue } from '../types'
import type { BrandAd } from './SponsoredBanner'
import { VenueCard } from './VenueCard'
import { Analytics } from '../services/analytics'
import { openExternal } from '../utils/openExternal'
import type { WeeklyFeature } from '../services/weeklyFeatureService'

/**
 * Happy Hour of the Week — one venue, one week, optional brand sponsor.
 *
 * Design intent (monetization principles): this must read as CURATION
 * first, advertising second. The venue is an editorial pick; the sponsor
 * "presents" it. Sponsor branding is a labeled strip, not a takeover.
 * Renders identically (minus the strip) when unsponsored, so the feature
 * runs — and accumulates the tap metrics that SELL the sponsorship —
 * before any brand signs.
 *
 * Tracking comes free: the embedded VenueCard fires card_view/detail_view
 * through the impression buffer. Sponsor impressions/clicks reuse the
 * brand_ads pipeline (ad_events via the server endpoint), so sponsor
 * reporting lives alongside ad reporting — one invoice story.
 */
export function WeeklyFeatureCard({
  feature,
  venue,
  sponsor,
  isFavorite,
  onToggleFavorite,
}: {
  feature: WeeklyFeature
  venue: Venue
  sponsor: BrandAd | null
  isFavorite: boolean
  onToggleFavorite: (venueId: string, venueName: string) => void
}) {
  // Sponsor impression: once per mount, only when the block is ≥50%
  // visible for 1s — same viewability standard as SponsoredBanner.
  const ref = React.useRef<HTMLDivElement | null>(null)
  const fired = React.useRef(false)
  React.useEffect(() => {
    const el = ref.current
    if (!el || !sponsor || fired.current) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        timer = setTimeout(() => {
          if (!fired.current) {
            fired.current = true
            Analytics.adImpression(sponsor.id, sponsor.brand_name)
          }
        }, 1000)
      } else if (timer) { clearTimeout(timer); timer = null }
    }, { threshold: 0.5 })
    obs.observe(el)
    return () => { obs.disconnect(); if (timer) clearTimeout(timer) }
  }, [sponsor])

  function handleSponsorClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (!sponsor) return
    Analytics.adClicked(sponsor.id, sponsor.brand_name)
    if (sponsor.click_url) openExternal(sponsor.click_url)
  }

  return (
    <div ref={ref} style={{ marginBottom: 18 }}>
      {/* Eyebrow */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 6, marginBottom: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 17 }}>🏆</span>
          <div>
            <div style={{
              fontSize: 12, fontWeight: 800, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: '#E85D1A',
            }}>
              Happy Hour of the Week
            </div>
            {feature.headline && (
              <div style={{ fontSize: 12, color: '#8A8578', marginTop: 1 }}>
                {feature.headline}
              </div>
            )}
          </div>
        </div>

        {sponsor && (
          <button
            onClick={handleSponsorClick}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#fff', border: '1px solid #EAE6DF', borderRadius: 20,
              padding: '4px 10px 4px 5px', cursor: 'pointer', fontSize: 11,
              color: '#8A8578',
            }}
          >
            {sponsor.logo_url && (
              <span style={{
                width: 20, height: 20, borderRadius: '50%', overflow: 'hidden',
                background: sponsor.logo_bg_color || '#fff', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <img src={sponsor.logo_url} alt={sponsor.brand_name}
                     style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </span>
            )}
            <span>
              Presented by <strong style={{ color: '#3A3630' }}>{sponsor.brand_name}</strong>
            </span>
          </button>
        )}
      </div>

      {/* The pick itself — a real venue card, full tracking included */}
      <VenueCard venue={venue} isFavorite={isFavorite} onToggleFavorite={onToggleFavorite} />
    </div>
  )
}
